import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldX, Clock3, ExternalLink, Link2Off, FlaskConical, CheckCircle2, XCircle, RotateCcw, Pencil } from "lucide-react";
import { api, relativeTime, exactTime } from "@/lib/api";

const STATUS_META = {
  approved: { label: "APPROVED", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: ShieldCheck },
  restricted: { label: "RESTRICTED", cls: "bg-sky-50 text-sky-700 border-sky-200", Icon: ShieldAlert },
  pending_review: { label: "PENDING REVIEW", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock3 },
  blocked: { label: "BLOCKED", cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: ShieldX },
};
const FIELDS = [
  ["automated_access", "Automated access"], ["link", "Link to original"], ["publisher_metadata", "Publisher & factual metadata (dates, author)"],
  ["headline", "Headline display"], ["summary", "AI-assisted summary"], ["excerpt", "Article excerpts"], ["images", "Publisher images / thumbnails"],
];
const PERM_CLS = { permitted: "text-emerald-700", not_permitted: "text-rose-700", pending: "text-amber-700" };
const inp = "w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-violet-300";
const err = (e) => toast.error(e.response?.data?.detail || "Action failed");

export function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending_review;
  return <span data-testid={`source-status-${status}`} className={`inline-flex items-center gap-1 text-[10px] font-mono font-bold tracking-wider px-2 py-0.5 rounded border ${m.cls}`}><m.Icon size={11} /> {m.label}</span>;
}

export function Registry() {
  const { data } = useQuery({ queryKey: ["admin-sources"], queryFn: async () => (await api.get("/admin/sources")).data });
  const [openId, setOpenId] = useState(null);
  return (
    <div>
      <h1 className="font-display font-bold text-2xl tracking-tight mb-1">Source Eligibility Registry</h1>
      <p className="text-sm text-muted-foreground mb-4 max-w-3xl">Each field is enforced separately at display time. Mark a field “permitted” only with documented evidence (official terms, licence agreement or authorized API). Automated checks here are not legal advice.</p>
      <div className="space-y-3">
        {data?.map((s) => (
          <div key={s.id} data-testid="registry-row" className="bg-white rounded-xl border border-slate-200">
            <button data-testid="registry-row-toggle" onClick={() => setOpenId(openId === s.id ? null : s.id)} className="w-full p-4 flex flex-col sm:flex-row sm:items-center gap-2 text-start">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-800">{s.name}</span>
                  <StatusPill status={s.eligibility_status} />
                  {s.review_overdue && <span className="text-[10px] font-mono text-rose-600">REVIEW OVERDUE</span>}
                  {s.robots_allowed === false && <span className="text-[10px] font-mono text-rose-600">ROBOTS DISALLOW</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">{s.homepage} · {s.source_type || "rss"} · next review {s.next_review_at ? relativeTime(s.next_review_at).replace(" ago", "") : "—"}</div>
              </div>
              <div className="flex flex-wrap gap-1.5 text-[10px] font-mono">
                {FIELDS.map(([f]) => <span key={f} className={PERM_CLS[s.permissions?.[f] || "pending"]}>{f}:{(s.permissions?.[f] || "pending").replace("_", " ")}</span>)}
              </div>
            </button>
            {openId === s.id && <RegistryEditor source={s} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function RegistryEditor({ source }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    eligibility_status: source.eligibility_status, permissions: { ...source.permissions }, terms_url: source.terms_url || "",
    license_info: source.license_info || "", attribution_requirements: source.attribution_requirements || "",
    commercial_restrictions: source.commercial_restrictions || "", evidence: source.evidence || "", source_type: source.source_type || "rss",
    review_interval_days: source.review_interval_days || 90, reason: "",
  });
  const [saving, setSaving] = useState(false);
  const { data: history } = useQuery({ queryKey: ["audit", source.id], queryFn: async () => (await api.get(`/admin/audit?entity_id=${source.id}&limit=10`)).data });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/sources/${source.id}/eligibility`, { ...f, review_interval_days: Number(f.review_interval_days) });
      const im = data.impact;
      toast.success(`Saved. ${im.withdrawn.length ? `Withdrawn: ${im.withdrawn.join(", ")} — items sent for review. ` : ""}${im.granted.length ? `Granted: ${im.granted.join(", ")} — processing items.` : ""}`);
      qc.invalidateQueries();
    } catch (x) { err(x); } finally { setSaving(false); }
  };
  const L = ({ children }) => <span className="block text-[11px] font-medium text-slate-500 mb-1">{children}</span>;

  return (
    <form data-testid="registry-editor" onSubmit={save} className="border-t border-slate-100 p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="space-y-3">
        <label className="block"><L>Eligibility status</L>
          <select data-testid="registry-status-select" value={f.eligibility_status} onChange={(e) => setF({ ...f, eligibility_status: e.target.value })} className={inp}>
            {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {FIELDS.map(([k, label]) => (
            <label key={k} className="block"><L>{label}</L>
              <select data-testid={`perm-${k}`} value={f.permissions[k] || "pending"} onChange={(e) => setF({ ...f, permissions: { ...f.permissions, [k]: e.target.value } })} className={`${inp} ${PERM_CLS[f.permissions[k] || "pending"]}`}>
                <option value="pending">Pending review</option><option value="permitted">Permitted</option><option value="not_permitted">Not permitted</option>
              </select>
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block"><L>Source type</L>
            <select value={f.source_type} onChange={(e) => setF({ ...f, source_type: e.target.value })} className={inp}>
              {["rss", "api", "licensed_provider", "crawler", "partner"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </label>
          <label className="block"><L>Review every (days)</L><input type="number" min={1} value={f.review_interval_days} onChange={(e) => setF({ ...f, review_interval_days: e.target.value })} className={inp} /></label>
        </div>
      </div>
      <div className="space-y-3">
        <label className="block"><L>Terms of use URL</L><input data-testid="registry-terms-url" value={f.terms_url} onChange={(e) => setF({ ...f, terms_url: e.target.value })} className={inp} placeholder="https://publisher.com/terms" /></label>
        <label className="block"><L>Licensing information</L><input value={f.license_info} onChange={(e) => setF({ ...f, license_info: e.target.value })} className={inp} /></label>
        <label className="block"><L>Attribution requirements</L><input value={f.attribution_requirements} onChange={(e) => setF({ ...f, attribution_requirements: e.target.value })} className={inp} /></label>
        <label className="block"><L>Commercial use / redistribution restrictions</L><input value={f.commercial_restrictions} onChange={(e) => setF({ ...f, commercial_restrictions: e.target.value })} className={inp} /></label>
        <label className="block"><L>Evidence supporting the decision (required to grant permissions)</L><textarea data-testid="registry-evidence" rows={3} value={f.evidence} onChange={(e) => setF({ ...f, evidence: e.target.value })} className={inp} /></label>
        <label className="block"><L>Reason for this change (recorded in audit log)</L><input data-testid="registry-reason" required minLength={3} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className={inp} /></label>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] text-muted-foreground">Last verified: {source.verified_at ? `${exactTime(source.verified_at)} by ${source.verified_by}` : "never"}</span>
          <button data-testid="registry-save" disabled={saving} className="bg-violet-600 text-white text-sm font-semibold px-5 py-2 rounded-full hover:bg-violet-700 disabled:opacity-60">Save decision</button>
        </div>
      </div>
      <div className="lg:col-span-2">
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">Decision history</div>
        <AuditList items={history} />
      </div>
    </form>
  );
}

export function ReviewQueue() {
  const { data } = useQuery({ queryKey: ["review-queue"], queryFn: async () => (await api.get("/admin/review-queue")).data });
  return (
    <div>
      <h1 className="font-display font-bold text-2xl tracking-tight mb-1">Review Queue</h1>
      <p className="text-sm text-muted-foreground mb-4">{data?.total ?? 0} items need human review · {data?.pending_sources ?? 0} sources pending review · {data?.overdue_sources ?? 0} sources overdue.</p>
      <div className="space-y-3">
        {data?.items?.map((a) => <ReviewItem key={a.id} a={a} />)}
        {!data?.items?.length && <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-muted-foreground">Nothing in the review queue.</div>}
      </div>
    </div>
  );
}

function ReviewItem({ a }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [editing, setEditing] = useState(false);
  const [summary, setSummary] = useState(a.summary || "");
  const done = (msg) => { toast.success(msg); qc.invalidateQueries(); };
  const decide = async (decision) => {
    if (reason.trim().length < 3) return toast.error("Enter a reason (recorded in audit log)");
    try { await api.post(`/admin/articles/${a.id}/review`, { decision, reason }); done(`Item ${decision}d`); } catch (x) { err(x); }
  };
  const run = async (path, msg) => { try { await api.post(`/admin/articles/${a.id}/${path}`); done(msg); } catch (x) { err(x); } };
  const saveSummary = async () => { try { await api.patch(`/admin/articles/${a.id}/summary`, { summary }); setEditing(false); done("Summary corrected"); } catch (x) { err(x); } };
  return (
    <div data-testid="review-item" className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1"><StatusPill status={a.source_status} /><span className="text-[11px] font-mono text-rose-600">{a.review_reason}</span></div>
          <div className="text-sm font-medium text-slate-800">{a.title}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{a.source_name} · discovered {relativeTime(a.retrieved_at)} · link: {a.link_status || "unchecked"}</div>
        </div>
        <a href={a.original_url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-violet-600" title="Open original"><ExternalLink size={16} /></a>
      </div>
      {a.factuality && (
        <div className={`mt-2 text-xs rounded-lg px-3 py-2 ${a.factuality.supported ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
          Factuality screening: {a.factuality.supported ? "supported" : "issues found"} ({a.factuality.score}/100){a.factuality.issues?.length > 0 && ` — ${a.factuality.issues.join("; ")}`}
        </div>
      )}
      {editing ? (
        <div className="mt-2 space-y-2"><textarea data-testid="review-summary-edit" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} className={inp} /><button data-testid="review-summary-save" type="button" onClick={saveSummary} className="text-xs font-semibold text-violet-700">Save correction</button></div>
      ) : a.summary && <p className="mt-2 text-sm text-slate-600">{a.summary}</p>}
      <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
        <input data-testid="review-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for decision (required)" className={`${inp} sm:max-w-xs`} />
        <div className="flex flex-wrap items-center gap-1">
          <Btn testid="review-approve" onClick={() => decide("approve")}><CheckCircle2 size={14} className="text-emerald-600" /> Clear</Btn>
          <Btn testid="review-remove" onClick={() => decide("remove")}><XCircle size={14} className="text-rose-600" /> Remove</Btn>
          <Btn testid="review-edit" onClick={() => setEditing(!editing)}><Pencil size={14} /> Correct</Btn>
          <Btn testid="review-factuality" onClick={() => run("factuality", "Factuality check complete")}><FlaskConical size={14} /> Fact-check</Btn>
          <Btn testid="review-check-link" onClick={() => run("check-link", "Link checked")}><Link2Off size={14} /> Check link</Btn>
        </div>
      </div>
    </div>
  );
}

const Btn = ({ children, onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">{children}</button>
);

export function BrokenLinks() {
  const qc = useQueryClient();
  const [running, setRunning] = useState(false);
  const { data } = useQuery({ queryKey: ["broken-links"], queryFn: async () => (await api.get("/admin/broken-links")).data });
  const runNow = async () => {
    setRunning(true);
    try { const r = (await api.post("/admin/link-check")).data; toast.success(`Checked ${r.checked} links · ${r.broken} broken`); qc.invalidateQueries(); } catch (x) { err(x); } finally { setRunning(false); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3">
        <div><h1 className="font-display font-bold text-2xl tracking-tight">Broken-Link Monitoring</h1><p className="text-sm text-muted-foreground">Runs daily in the background. Broken items are sent to the review queue (never auto-deleted).</p></div>
        <button data-testid="run-link-check" onClick={runNow} disabled={running} className="shrink-0 inline-flex items-center gap-1.5 bg-violet-600 text-white text-sm font-semibold px-4 py-2 rounded-full disabled:opacity-60"><RotateCcw size={14} className={running ? "animate-spin" : ""} /> Check now</button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {data?.map((a) => (
          <div key={a.id} data-testid="broken-link-row" className="p-3.5 text-sm flex items-center justify-between gap-3">
            <div className="min-w-0"><div className="font-medium text-slate-800 line-clamp-1">{a.title}</div><div className="text-xs text-muted-foreground truncate">{a.original_url}</div></div>
            <span className={`shrink-0 font-mono text-xs ${a.link_status === "broken" ? "text-rose-600" : "text-amber-600"}`}>{a.link_status} {a.link_http_status || ""}</span>
          </div>
        ))}
        {!data?.length && <div className="p-8 text-center text-sm text-muted-foreground">No broken or failing links recorded.</div>}
      </div>
    </div>
  );
}

export function AuditList({ items }) {
  if (!items?.length) return <div className="text-xs text-muted-foreground">No audit entries.</div>;
  return (
    <ul className="divide-y divide-slate-100 text-xs">
      {items.map((l) => (
        <li key={l.id} data-testid="audit-row" className="py-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
          <span className="font-mono text-violet-700 shrink-0">{l.action}</span>
          <span className="text-slate-600 flex-1 min-w-0 truncate">{l.reason || ""} {l.details?.name || l.details?.source || l.details?.title || ""}{l.details?.withdrawn?.length ? ` · withdrawn: ${l.details.withdrawn.join(", ")}` : ""}{l.details?.granted?.length ? ` · granted: ${l.details.granted.join(", ")}` : ""}</span>
          <span className="text-muted-foreground shrink-0" title={exactTime(l.at)}>{l.actor} · {relativeTime(l.at)}</span>
        </li>
      ))}
    </ul>
  );
}

export function AuditLog() {
  const [action, setAction] = useState("");
  const { data } = useQuery({ queryKey: ["audit-all", action], queryFn: async () => (await api.get(`/admin/audit?limit=200${action ? `&action=${action}` : ""}`)).data });
  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3">
        <h1 className="font-display font-bold text-2xl tracking-tight">Audit History</h1>
        <select data-testid="audit-filter" value={action} onChange={(e) => setAction(e.target.value)} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg">
          <option value="">All actions</option>
          {["source_permissions_changed", "article_ingested", "article_review_approve", "article_review_remove", "summary_corrected", "user_report", "link_broken", "ingestion_skipped", "factuality_check", "settings_updated"].map((x) => <option key={x}>{x}</option>)}
        </select>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-4"><AuditList items={data} /></div>
    </div>
  );
}

export function SettingsPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const toggle = async () => {
    try { await api.patch("/admin/settings", { beta_enabled: !data?.beta_enabled }); toast.success("Settings saved"); qc.invalidateQueries(); } catch (x) { err(x); }
  };
  return (
    <div>
      <h1 className="font-display font-bold text-2xl tracking-tight mb-4">Site Settings</h1>
      <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between gap-4">
        <div><div className="font-semibold text-slate-800">BETA badge & notice</div><div className="text-sm text-muted-foreground">Turn off when NxtTechNews is ready for its official release.</div></div>
        <button data-testid="toggle-beta" onClick={toggle} className={`px-4 py-2 rounded-full text-sm font-semibold ${data?.beta_enabled ? "bg-violet-600 text-white" : "bg-slate-200 text-slate-700"}`}>{data?.beta_enabled ? "Beta ON" : "Beta OFF"}</button>
      </div>
    </div>
  );
}
